import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  AlertCircle,
  Filter,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { queryClient } from "@/lib/queryClient";

type DocAccessRow = {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  userId: string | null;
  action: string;
  fileName: string | null;
  fileSize: number | null;
  objectKey: string | null;
  threat: string | null;
  errorMessage: string | null;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
};

type PlatformDocAccessResponse = {
  rows: DocAccessRow[];
  count: number;
  limit: number;
  offset: number;
};

const ACTION_OPTIONS = [
  { value: "all", label: "All actions" },
  { value: "upload", label: "Upload" },
  { value: "upload_rejected", label: "Upload rejected" },
  { value: "download", label: "Download" },
  { value: "view", label: "View" },
  { value: "preview", label: "Preview" },
  { value: "email_attached", label: "Emailed" },
  { value: "delete", label: "Delete" },
  { value: "virus_scan_failed", label: "Virus scan failed" },
  { value: "decrypt_error", label: "Decrypt error" },
];

const VERDICT_OPTIONS = [
  { value: "all", label: "All verdicts" },
  { value: "clean", label: "Clean" },
  { value: "infected", label: "Infected" },
  { value: "error", label: "Error" },
];

const PAGE_SIZE = 100;

function ScanResult({ row }: { row: DocAccessRow }) {
  const meta = row.metadata as Record<string, unknown> | null;
  const verdict = typeof meta?.verdict === "string" ? meta.verdict : null;
  const scanner = typeof meta?.scanner === "string" ? meta.scanner : null;

  if (row.action === "upload_rejected") {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs">
        <XCircle className="w-3.5 h-3.5 shrink-0" />
        Rejected
      </span>
    );
  }
  if (row.action === "decrypt_error") {
    return (
      <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 text-xs">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        Decrypt error
      </span>
    );
  }
  if (row.action === "virus_scan_failed") {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs">
        <XCircle className="w-3.5 h-3.5 shrink-0" />
        Scan failed
        {row.threat ? ` (${row.threat})` : ""}
      </span>
    );
  }
  if (!verdict) {
    return <span className="text-muted-foreground text-xs italic">—</span>;
  }
  if (verdict === "clean") {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs">
        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
        {scanner ? `${scanner} clean` : "Clean"}
      </span>
    );
  }
  if (verdict === "infected") {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs">
        <XCircle className="w-3.5 h-3.5 shrink-0" />
        {scanner ? `${scanner} infected` : "Infected"}
        {row.threat ? ` — ${row.threat}` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 text-xs">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
      {scanner ? `${scanner} ${verdict}` : verdict}
    </span>
  );
}

function actionBadge(action: string) {
  const severe = new Set(["virus_scan_failed", "upload_rejected", "decrypt_error"]);
  if (severe.has(action)) {
    return <Badge variant="destructive" className="text-[11px]">{action}</Badge>;
  }
  if (action === "upload") {
    return <Badge variant="secondary" className="text-[11px]">upload</Badge>;
  }
  return <Badge variant="outline" className="text-[11px]">{action}</Badge>;
}

export function PlatformDocumentSecurityContent() {
  const [workspaceId, setWorkspaceId] = useState("");
  const [userId, setUserId] = useState("");
  const [action, setAction] = useState("all");
  const [verdict, setVerdict] = useState("all");
  const [sinceDate, setSinceDate] = useState("");
  const [untilDate, setUntilDate] = useState("");
  const [page, setPage] = useState(0);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (workspaceId.trim()) p.set("workspaceId", workspaceId.trim());
    if (userId.trim()) p.set("userId", userId.trim());
    if (action !== "all") p.set("action", action);
    if (verdict !== "all") p.set("verdict", verdict);
    if (sinceDate) p.set("since", new Date(`${sinceDate}T00:00:00`).toISOString());
    if (untilDate) p.set("until", new Date(`${untilDate}T23:59:59.999`).toISOString());
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String(page * PAGE_SIZE));
    return p.toString();
  }, [workspaceId, userId, action, verdict, sinceDate, untilDate, page]);

  const queryKey = ["/api/platform/security/document-access", queryParams];

  const { data, isLoading, error, isFetching } = useQuery<PlatformDocAccessResponse>({
    queryKey,
    queryFn: async () => {
      const res = await fetch(`/api/platform/security/document-access?${queryParams}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load document access log: ${res.status}`);
      return res.json();
    },
  });

  const rows = data?.rows ?? [];
  const hasNext = rows.length >= PAGE_SIZE;
  const hasFilters = workspaceId.trim() || userId.trim() || action !== "all" || verdict !== "all" || sinceDate || untilDate;

  function resetFilters() {
    setWorkspaceId("");
    setUserId("");
    setAction("all");
    setVerdict("all");
    setSinceDate("");
    setUntilDate("");
    setPage(0);
  }

  function resetPage() {
    setPage(0);
  }

  return (
    <div className="space-y-6" data-testid="panel-platform-document-security">
      <div>
        <h2 className="text-xl font-semibold">Document Access & Scan Feed</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Full cross-workspace document access log — uploads, downloads, scan verdicts,
          and error events across all tenants. Filter by workspace or user to drill in.
        </p>
      </div>

      <Card data-testid="card-platform-doc-filters">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Workspace ID</Label>
              <Input
                value={workspaceId}
                onChange={(e) => { setWorkspaceId(e.target.value); resetPage(); }}
                placeholder="all workspaces"
                className="h-8 text-sm"
                data-testid="input-platform-doc-workspace"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">User ID</Label>
              <Input
                value={userId}
                onChange={(e) => { setUserId(e.target.value); resetPage(); }}
                placeholder="all users"
                className="h-8 text-sm"
                data-testid="input-platform-doc-userid"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Action</Label>
              <Select value={action} onValueChange={(v) => { setAction(v); resetPage(); }}>
                <SelectTrigger className="h-8 text-sm" data-testid="select-platform-doc-action">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Verdict</Label>
              <Select value={verdict} onValueChange={(v) => { setVerdict(v); resetPage(); }}>
                <SelectTrigger className="h-8 text-sm" data-testid="select-platform-doc-verdict">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VERDICT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Since</Label>
              <Input
                type="date"
                value={sinceDate}
                onChange={(e) => { setSinceDate(e.target.value); resetPage(); }}
                className="h-8 text-sm"
                data-testid="input-platform-doc-since"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Until</Label>
              <Input
                type="date"
                value={untilDate}
                onChange={(e) => { setUntilDate(e.target.value); resetPage(); }}
                className="h-8 text-sm"
                data-testid="input-platform-doc-until"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            {hasFilters && (
              <Button
                size="sm"
                variant="ghost"
                onClick={resetFilters}
                className="h-7 text-xs"
                data-testid="button-platform-doc-reset"
              >
                <Filter className="w-3 h-3 mr-1" />
                Clear filters
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => queryClient.invalidateQueries({ queryKey: ["/api/platform/security/document-access"] })}
              disabled={isFetching}
              className="h-7 text-xs"
              data-testid="button-platform-doc-refresh"
            >
              {isFetching ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3 mr-1" />
              )}
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : error ? (
        <Alert variant="destructive" data-testid="alert-platform-doc-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{(error as Error).message}</AlertDescription>
        </Alert>
      ) : rows.length === 0 ? (
        <p
          className="text-sm text-muted-foreground py-8 text-center"
          data-testid="text-platform-doc-empty"
        >
          No document access records found{hasFilters ? " matching these filters" : ""}.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border" data-testid="table-platform-doc-access">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-xs text-muted-foreground border-b">
                  <th className="px-3 py-2 text-left font-medium">Time</th>
                  <th className="px-3 py-2 text-left font-medium">Workspace</th>
                  <th className="px-3 py-2 text-left font-medium">User</th>
                  <th className="px-3 py-2 text-left font-medium">Action</th>
                  <th className="px-3 py-2 text-left font-medium">File</th>
                  <th className="px-3 py-2 text-left font-medium">Scan result</th>
                  <th className="px-3 py-2 text-left font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                    data-testid={`row-platform-doc-${row.id}`}
                  >
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(row.createdAt), "MMM d, yyyy HH:mm:ss")}
                    </td>
                    <td className="px-3 py-2 text-xs truncate max-w-[180px]">
                      {row.workspaceName ? (
                        <span title={`${row.workspaceName} (${row.workspaceId})`}>
                          <span className="font-medium">{row.workspaceName}</span>
                          <span className="text-muted-foreground font-mono ml-1">
                            ({row.workspaceId.slice(0, 12)}{row.workspaceId.length > 12 ? "…" : ""})
                          </span>
                        </span>
                      ) : (
                        <span className="font-mono" title={row.workspaceId}>
                          {row.workspaceId.slice(0, 16)}{row.workspaceId.length > 16 ? "…" : ""}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs truncate max-w-[120px]">
                      {row.userId ? (
                        <span title={row.userId}>
                          {row.userId.slice(0, 16)}{row.userId.length > 16 ? "…" : ""}
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic">system</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {actionBadge(row.action)}
                    </td>
                    <td className="px-3 py-2 truncate max-w-[160px] text-xs" title={row.fileName ?? undefined}>
                      {row.fileName ? (
                        <span>{row.fileName}</span>
                      ) : row.objectKey ? (
                        <span className="font-mono text-muted-foreground">{row.objectKey.split("/").pop()}</span>
                      ) : (
                        <span className="text-muted-foreground italic">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <ScanResult row={row} />
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground font-mono">
                      {row.ipAddress ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + rows.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || isFetching}
                data-testid="button-platform-doc-prev"
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNext || isFetching}
                data-testid="button-platform-doc-next"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
